import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import Lightbox from '../components/Lightbox';
import { fetchCollection, fetchLocalizedCollection } from '../lib/cms';

export default function AboutPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlug = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find((s) => getSlug(s) === 'om-os');
      if (match) setPageData(match);
    });

    fetchLocalizedCollection('historiske_milepaele', 'da').then((items) => {
      if (items && items.length) {
        const sorted = items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
        setMilestones(
          sorted.map((m) => {
            const rawDesc = m.beskrivelse || '';
            const plainDesc = rawDesc.replace(/<[^>]+>/g, '').trim();
            const img = Array.isArray(m.billede) ? m.billede[0] : m.billede;
            return {
              year: m.aarstal,
              title: m.titel,
              desc: plainDesc || rawDesc,
              img: img || '',
            };
          })
        );
      }
    });
  }, []);

  const defaultMilestones = [
    {
      year: '1921',
      title: 'Fabrikkens medarbejdere foran værkstedet',
      desc: 'Foran den hvide bygning med de karakteristiske buede porte står fabrikkens medarbejdere samlet. Det er både mestre, svende og lærlinge – nogle helt unge drenge, andre med års erfaring i hænderne. Billedet udstråler fællesskab, slid og faglig stolthed, og er et stærkt vidnesbyrd om de mennesker, der bar virksomheden frem.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/medarbejdere-1921.webp',
    },
    {
      year: '1943',
      title: 'Maskinarbejdere og remtræk i hallen',
      desc: 'Et kig ind i maskinhallen, hvor remtræk fra loftet driver presser, drejebænke og fræsere. Stemningen er intens, og man fornemmer den travle hverdag med unge arbejdere i kedeldragter, der betjener maskinerne. Det var i denne tid, at fabrikken for alvor fandt sit industrielle udtryk og skabte fundamentet for fremtidens vækst.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskinarbejdere-pmb-1943.webp',
    },
    {
      year: '1930',
      title: 'Milepæl: Presse nummer 1000 fejres',
      desc: 'På dette billede fejres en stor milepæl: produktionen af presse nr. 1000. Arbejdere, funktionærer og gæster er samlet omkring maskinen, og stemningen er højtidelig. For fabrikken var det et symbol på vækst, kvalitet og stabil produktion – og en markering af, at man havde skabt en maskine, der for alvor havde sat sit præg på dansk industri.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/ekscenterpresser-1930-presse-nummer-1000.webp',
    },
    {
      year: '1965',
      title: 'Fuldt industrialiseret produktion',
      desc: 'Her springer vi frem i tiden til midten af 1960’erne. Fabrikken er blevet moderne, og maskinerne står store og tunge i rækker. Loftet er højt, belysningen skarp, og maskinerne langt mere avancerede end de tidlige remtræksanlæg. Billedet viser overgangen fra håndværkspræget industri til en fuldt industrialiseret produktion, der gjorde Povl Møllers Maskinfabrik konkurrencedygtig på det internationale marked.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pressere-1965.webp',
    },
    {
      year: '1930\'erne',
      title: 'Innovation: Autolifte og Ford T',
      desc: 'Selvom ekscenterpressen blev vores kendemærke, udviklede Povl Møllers Maskinfabrik også andre løsninger til industrien. Blandt de mere spektakulære var autolifte, som gjorde det muligt at hæve hele biler – her en Ford fra 1930’erne – højt til vejrs. Et billede på den opfindsomhed og tekniske kunnen, som altid har kendetegnet PMB.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/ford-t-og-autolifte-fra-pmb.webp',
    },
  ];

  const historicalMilestones = milestones.length > 0 ? milestones : defaultMilestones;

  return (
    <>
      <Helmet>
        <title>{pageData?.seo_titel || 'PMB Historie – Fra værksted til maskinfabrik siden 1921'}</title>
        <meta
          name="description"
          content={
            pageData?.meta_beskrivelse ||
            'PMB Historie – Fra værksted til maskinfabrik siden 1921. Læs historien om Povl Møllers Maskinfabrik i Borup.'
          }
        />
        <meta
          property="og:title"
          content={pageData?.seo_titel || 'PMB Historie – Fra værksted til maskinfabrik siden 1921'}
        />
        <meta
          property="og:description"
          content={
            pageData?.intro ||
            'Gennem årtier blev værkstedet til Povl Møllers Maskinfabrik – og i dag lever navnet videre som PMB.'
          }
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Om os</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Der var engang…</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              Vores Historie
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Der var engang...
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-stone-300 leading-relaxed">
              <strong>Povl Møllers Maskinfabrik</strong> begyndte som et lille værksted i begyndelsen af 1900-tallet. Her var håndværket i centrum, og produktionen blev drevet af remtræk, muskelkraft og stærk faglig stolthed.
            </p>
            <p className="mt-3 text-sm sm:text-base text-stone-400 leading-relaxed">
              Med tiden voksede fabrikken – både i antal medarbejdere og i teknologisk udvikling – og blev kendt for driftssikre og holdbare ekscenterpressere, som blev eksporteret til hele verden.
            </p>
          </div>
        </div>
      </section>

      {/* Historical Milestones Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {pageData?.indhold && (
            <div className="max-w-4xl mx-auto p-8 rounded-xl bg-stone-50 border border-stone-200">
              <div
                className="cms-content text-stone-700 text-base sm:text-lg leading-relaxed space-y-4 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: pageData.indhold }}
              />
            </div>
          )}
          {historicalMilestones.map((item, index) => {
            const isEven = index % 2 === 0;
            return (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  isEven ? '' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Text description */}
                <div className={`lg:col-span-6 ${isEven ? 'lg:order-1' : 'lg:order-2'} space-y-4`}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-stone-100 text-[#006368] text-xs font-bold uppercase tracking-wider">
                    <i className="fa-solid fa-clock-rotate-left text-xs" />
                    <span>Omkring {item.year}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                    {item.title}
                  </h2>
                  <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                {/* Historical photo with interactive lightbox trigger */}
                <div className={`lg:col-span-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                    className="relative group rounded-lg overflow-hidden border border-stone-200 shadow-md bg-stone-100 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    aria-label={`Forstør foto: ${item.title}`}
                  >
                    <CmsImage
                      src={item.img}
                      alt={item.title}
                      className="w-full h-auto object-cover max-h-[480px] group-hover:scale-102 transition-transform duration-300 filter grayscale contrast-110"
                      width={800}
                    />
                    <div className="absolute inset-0 bg-[#006368]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <span className="bg-[#1a2024]/90 px-4 py-2 rounded text-xs font-semibold flex items-center gap-2">
                        <i className="fa-solid fa-magnifying-glass-plus" />
                        <span>Klik for stor historisk visning</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Epilogue Statement */}
      <section className="py-20 bg-stone-50 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
            Kontinuitet & Fremtid
          </span>
          <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 tracking-tight">
            Eventyret er ikke forbi.
          </h2>
          <div className="text-stone-700 text-base sm:text-lg leading-relaxed space-y-4 max-w-2xl mx-auto">
            <p>
              Der var engang et værksted, hvor gnisterne fra stålet satte drømme i gang.
              Drømme, der voksede til maskiner, og maskiner, der voksede til en hel fabrik.
            </p>
            <p>
              Gennem årtier blev værkstedet til Povl Møllers Maskinfabrik – og i dag lever navnet videre som <strong>PMB</strong>.
            </p>
            <p className="font-semibold text-stone-900">
              For hver presse, hver løsning og hver kunde skrives der nye kapitler – stadig med de samme værdier: kvalitet, pålidelighed og innovation.
            </p>
            <p className="text-[#006368] font-bold text-lg">
              Og sådan lever historien videre. Fra før til nu. Fra værksted til PMB.
            </p>
          </div>

          <div className="pt-6 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/pmb-partnere"
              className="px-6 py-3 rounded bg-[#006368] text-white text-sm font-semibold hover:bg-[#004d51] transition shadow-sm"
            >
              Mød vores partnere
            </Link>
            <Link
              to="/kontakt"
              className="px-6 py-3 rounded border border-stone-300 bg-white text-stone-800 text-sm font-semibold hover:bg-stone-100 transition"
            >
              Kontakt PMB
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        images={historicalMilestones.map((m) => ({ url: m.img, title: `${m.year} – ${m.title}` }))}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
